import React from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { LDFlagSet } from "launchdarkly-react-client-sdk";
import {
  AccountType,
  ActionList,
  DropdownInput,
  InputSizes,
  IOption,
  PageHeader,
  PaginationControl,
  Search,
  Head,
  PerformerAppPath,
  PerformerAppSmartBanner,
  RouterProps,
  isSmallDeviceWidth,
  withAccessControl,
} from "component-library";
import { withTranslation, WithTranslation } from "i18n";
import {
  JobsFeedUsageEvents,
  LaunchDarklyClientWithFlags,
  sendJobsfeedUsageHeapEventWithUserPrefix,
  withLaunchDarklyConsumerHOC,
} from "shared-services";
import { facetGroupCode } from "shared/config/common";
import scrollManager from "shared/services/scrollManager";
import { MapDispatchToProps } from "shared/types/utilities";

import {
  ALL_PAID_FULL_PATH,
  PAGINATION_PAGE_RANGE_DESKTOP,
  PAGINATION_PAGE_RANGE_MOBILE,
  PageTitleKey,
} from "config/common";
import PageKey from "config/PageKey";

import { IApplicationState } from "store";
import { hidePopup, setLoading, showPopup } from "store/ui/ui.actions";
import { HidePopup, SetLoading, ShowPopup } from "store/ui/ui.types";

import { IJobFilterParams } from "models/api";
import { IJobItem, IPaginatedJobItemList } from "models/job";

import { getPageTitle } from "helpers/getPageTitle";
import jobService, { SetJobActionType } from "services/api/job/jobService";

import { GTMEvents } from "app-shell/GoogleTagManager/GTMEvents";

import withJobsFilter, {
  IWithJobsFilterOptions,
  IWithJobsFilterProps,
} from "hoc/withJobsFilter/withJobsFilter";
import {
  FilterType,
  IJobSender,
  OpenClosedRoles,
  SortingType,
} from "hoc/withJobsFilter/withJobsFilter.config";

import { JobsPageTab, SEARCH_MAX_LENGTH } from "pages/JobsPage/JobsPage.config";
import { JobItemToJobTableItemConverter } from "pages/JobsPage/shared/shared.converters";
import { prepareJobItemToJobTableItemConverterTexts } from "pages/JobsPage/shared/shared.helpers";

import JobsTable, { IJobsTableTexts } from "components/JobsTable/JobsTable";
import { prepareTableTexts } from "components/JobsTable/JobsTable.helpers";
import {
  IJobTableItem,
  IJobTableItemTag,
  JobTableItemUuid,
} from "components/JobsTable/JobsTable.models";

import { IJobsPageInjectedProps } from "pages/JobsPage/JobsPage";
import {
  getOpenClosedRolesFilterOptions,
  getOpenClosedRolesFilter,
  INPUT_NAMES,
} from "pages/JobsPage/JobsPage.helpers";
import { setJobsFeedData } from "store/jobsFeed/jobsFeed.actions";
import { SetJobsFeedData, IJobsFeedState } from "store/jobsFeed/jobsFeed.types";
import {
  DEFAULT_JOBS_FEEED,
  ALL_PAID_SEARCH,
  persistSearchAndResetJobsFeedStorage,
  searchRefClear,
  initSearch,
  getInitialValue,
} from "shared/helpers/common";
import RefreshButton from "components/RefreshButton/RefreshButton";
import SelectAllControl from "components/SelectAllControl/SelectAllControl";
import PaginationResultStats from "components/PaginationResultStats/PaginationResultStats";

export interface IAllPaidPageTexts {
  table: IJobsTableTexts;
  pageTitle: string;
}

export interface IAllPaidPageStoreProps {
  texts: IAllPaidPageTexts;
  convertToTableItem: (
    jobItem: IJobItem,
    ldFlags?: LDFlagSet,
    userType?: AccountType
  ) => IJobTableItem;
  findJobs(
    params: IJobFilterParams,
    abortController?: AbortController
  ): Promise<IPaginatedJobItemList>;
  markAsRead: SetJobActionType;
  markAsUnread: SetJobActionType;
  isPerformer?: boolean;
  jobsFeed: IJobsFeedState;
}

export interface IAllPaidPageActions {
  setLoading: SetLoading;
  showPopup: ShowPopup;
  hidePopup: HidePopup;
  setJobsFeedData: SetJobsFeedData;
}

export interface IAllPaidPageProps
  extends
    IAllPaidPageStoreProps,
    IJobsPageInjectedProps,
    RouterProps,
    IAllPaidPageActions,
    IWithJobsFilterProps,
    LaunchDarklyClientWithFlags,
    WithTranslation {}

export interface IAllPaidPageState {
  selectedItems: JobTableItemUuid[];
}

export class AllPaidPage extends React.Component<
  IAllPaidPageProps,
  IAllPaidPageState
> {
  constructor(props: any) {
    super(props);

    this.setJobsFeedStorage(
      this.props.router.location.pathname,
      DEFAULT_JOBS_FEEED.PAGENUMBER,
      DEFAULT_JOBS_FEEED.BREAKDOWN_ID,
      this.props.jobsFeed.isBackToJobsFeed
    );

    const pageNumber = this.props.jobsFeed.pageNumber;

    if (this.props.jobsFeed.isBackToJobsFeed && pageNumber) {
      const [, setSearchParams] = this.props.router.searchParams;

      setSearchParams({ page: pageNumber });
    }
  }

  public static pageSize: number = 50;
  public readonly state: Readonly<IAllPaidPageState> = {
    selectedItems: [],
  };

  private readonly searchRef = React.createRef<Search>();

  public componentDidMount() {
    this.initSearch();
  }

  public render() {
    const {
      filterValues,
      paginatedItems,
      isLoading,
      commonTexts,
      texts,
      renderJobsFilterControl,
      isPerformer,
    } = this.props;
    const { selectedItems } = this.state;

    return (
      <section>
        <Head registerPageView={this.registerPageView}>
          <title>{texts.pageTitle}</title>
        </Head>
        <PerformerAppSmartBanner appPath={PerformerAppPath.AllPaid} />
        <PageHeader
          pageTitle={commonTexts.title}
          bottomSection={this.renderBottomSection()}
          actionSection={
            <ActionList variant="pageHeader" items={[]} moreButtonText="" />
          }
        />
        {renderJobsFilterControl(this.renderPagination())}
        <div className="g-content-area mt-1">
          <div className="g-col-lg-12">
            <div className="flex flex-col-reverse flex-wrap items-start justify-between gap-3 md:flex-row">
              <div className="flex flex-col gap-2">
                <PaginationResultStats
                  page={filterValues[FilterType.PageNumber]}
                  itemsPerPage={AllPaidPage.pageSize}
                  itemsTotal={paginatedItems.count}
                  textTemplate={texts.table.resultStats}
                />
                <SelectAllControl
                  selectItems={this.selectItems}
                  deselectItems={this.deselectItems}
                  selectedItems={selectedItems}
                  markItemsAsRead={this.markSelectedItemsAsRead}
                  markItemsAsUnread={this.markSelectedItemsAsUnread}
                  items={paginatedItems.items}
                  onSelectAllClick={() =>
                    sendJobsfeedUsageHeapEventWithUserPrefix(
                      JobsFeedUsageEvents.SelectAll
                    )
                  }
                />
              </div>
              <div className="w-full md:flex md:w-auto md:justify-end md:gap-3">
                <div className="grid grid-cols-2 gap-3 md:contents">
                  <div className="min-w-0">{this.renderOpenClosedRoles()}</div>
                  <div className="min-w-0">{this.renderSorter()}</div>
                </div>
                <div className="mt-3 flex justify-end md:mt-0 md:contents">
                  <RefreshButton
                    onClick={this.handleRefresh}
                    className="!flex !aspect-square !h-10 !w-10 !items-center !justify-center !gap-[10px] !rounded-[10px] !border !border-grey-light-2"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="g-content-area">
          <div className="g-col-lg-12 g-table-wrapper">
            <JobsTable
              texts={texts.table}
              items={paginatedItems.items}
              selectedItems={selectedItems}
              deselectItems={this.deselectItems}
              selectItems={this.selectItems}
              onSenderClick={this.handleSenderClick}
              onTagClick={this.handleTagClick}
              itemsPerPage={AllPaidPage.pageSize}
              isPageLoading={isLoading}
              pageNumber={filterValues[FilterType.PageNumber]}
              itemsCount={paginatedItems.count}
              updatePageNumber={this.handleUpdatePageNumber}
              onMarkAsRead={this.handleMarkAsRead}
              onMarkAsUnread={this.handleMarkAsUnread}
              onRefresh={this.handleRefresh}
              returnUri={ALL_PAID_FULL_PATH}
              isPerformer={isPerformer}
              jobsFeed={this.props.jobsFeed}
              setJobsFeedData={this.props.setJobsFeedData}
              showSelectAllControl={false}
            />
          </div>
        </div>
      </section>
    );
  }

  private renderBottomSection = () => {
    const { commonTexts, filterValues } = this.props;

    return (
      <div className="g-inner-grid-12 g-row-gap-sm g-column-gap-sm">
        <div className="g-col-lg-6">
          <Search
            ref={this.searchRef}
            placeholder={commonTexts.searchPlaceholder}
            maxLength={SEARCH_MAX_LENGTH}
            onSearch={this.handleSearch}
            onClear={this.handleSearchClear}
            initialValue={getInitialValue(filterValues)}
          />
        </div>
      </div>
    );
  };

  private renderSorter() {
    const { commonTexts, filterValues } = this.props;

    return (
      <DropdownInput<string>
        id="paidPageSorting"
        name={INPUT_NAMES.sortingType}
        size={InputSizes.Medium}
        className="[&_.c-input-wrapper]:!mb-0"
        variant="select"
        options={commonTexts.sortingOptions}
        selected={commonTexts.sortingOptions.find(
          (option) => option.value === filterValues[FilterType.SortingType]
        )}
        value={filterValues[FilterType.SortingType]}
        optionSelected={this.handleSortingTypeChange}
      />
    );
  }

  private renderOpenClosedRoles() {
    const { t } = this.props;
    const openClosedRoleOptions = getOpenClosedRolesFilterOptions(t);

    return getOpenClosedRolesFilter(
      this.props,
      (option: IOption<OpenClosedRoles>) => {
        sendJobsfeedUsageHeapEventWithUserPrefix(
          JobsFeedUsageEvents.FilterShowOpenClose
        );
        this.props.changeFilters({
          [FilterType.OpenClosedRoles]: option.value,
        });
      },
      openClosedRoleOptions,
      InputSizes.Medium,
      "[&_.c-input-wrapper]:!mb-0"
    );
  }

  private renderPagination() {
    const { paginatedItems, filterValues } = this.props;
    const pageNumber = filterValues[FilterType.PageNumber];
    const pageCount = Math.ceil(paginatedItems.count / AllPaidPage.pageSize);

    return (
      <div className="ml-auto">
        <PaginationControl
          handlePageChange={this.handleUpdatePageNumber}
          pageRangeDisplayed={
            isSmallDeviceWidth()
              ? PAGINATION_PAGE_RANGE_MOBILE
              : PAGINATION_PAGE_RANGE_DESKTOP
          }
          pageCount={pageCount}
          selectedPage={pageNumber}
          initialSelectedPage={pageNumber}
          arePageBreaksMandatory
        />
      </div>
    );
  }

  private readonly setJobsFeedStorage = (
    pathname: string,
    pageNumber: string,
    breakdownId: string,
    isBackToJobsFeed: boolean
  ) => {
    this.props.setJobsFeedData({
      pathname,
      pageNumber,
      breakdownId,
      isBackToJobsFeed,
    });
  };

  private handleUpdatePageNumber = (page: number) => {
    const { router } = this.props;
    const [, setSearchParams] = router.searchParams;

    this.setJobsFeedStorage(
      this.props.jobsFeed.pathname,
      String(page),
      DEFAULT_JOBS_FEEED.BREAKDOWN_ID,
      DEFAULT_JOBS_FEEED.IS_BACK_TO_JOBS_FEED
    );

    setSearchParams({ page: String(page) });
  };

  private handleSortingTypeChange = (option: IOption<SortingType>) => {
    sendJobsfeedUsageHeapEventWithUserPrefix(JobsFeedUsageEvents.FilterSort);
    this.props.changeFilters({ [FilterType.SortingType]: option.value });
  };

  private handleSenderClick = (sender: IJobSender) => {
    scrollManager.reset();
    this.props.changeFilters({ [FilterType.Sender]: sender });
  };

  private handleTagClick = (tag: IJobTableItemTag) => {
    scrollManager.reset();
    this.props.addFilterValue(tag.filterType, tag.facetId);
  };

  private handleSearch = (search: string | undefined) => {
    this.deselectAllItems();
    persistSearchAndResetJobsFeedStorage({
      setJobsFeedStorage: this.setJobsFeedStorage,
      pathName: this.props.router.location.pathname,
      tab: ALL_PAID_SEARCH,
      search,
    });

    this.props.changeFilters({ [FilterType.TextFilter]: search });
  };

  private handleSearchClear = () => {
    searchRefClear(this.searchRef);
    this.handleSearch(undefined);
  };

  private selectItems = (itemIds: JobTableItemUuid[]) => {
    this.setState({ selectedItems: [...this.state.selectedItems, ...itemIds] });
  };

  private deselectItems = (itemIds: JobTableItemUuid[]) => {
    this.setState({
      selectedItems: this.state.selectedItems.filter(
        (item) => !itemIds.includes(item)
      ),
    });
  };

  private deselectAllItems = () => {
    this.setState({ selectedItems: [] });
  };

  private handleMarkAsRead = async (itemIds: JobTableItemUuid[]) => {
    await this.props.onMarkAsRead(itemIds);
    this.deselectAllItems();
    sendJobsfeedUsageHeapEventWithUserPrefix(JobsFeedUsageEvents.MarkAsRead);
  };

  private handleMarkAsUnread = async (itemIds: JobTableItemUuid[]) => {
    await this.props.onMarkAsUnread(itemIds);
    this.deselectAllItems();
    sendJobsfeedUsageHeapEventWithUserPrefix(JobsFeedUsageEvents.MarkAsUnread);
  };

  private markSelectedItemsAsRead = (itemIds: JobTableItemUuid[]) => {
    void this.handleMarkAsRead(itemIds);
  };

  private markSelectedItemsAsUnread = (itemIds: JobTableItemUuid[]) => {
    void this.handleMarkAsUnread(itemIds);
  };

  private handleRefresh = () => {
    this.props.changeFilters({
      [FilterType.PageNumber]: this.props.filterValues[FilterType.PageNumber],
    });
    this.props.refreshCounters();
    sendJobsfeedUsageHeapEventWithUserPrefix(JobsFeedUsageEvents.Refresh);
  };

  private initSearch() {
    initSearch(
      ALL_PAID_SEARCH,
      this.props.jobsFeed.pageNumber,
      this.props.changeFilters
    );
  }

  private registerPageView = () => {
    GTMEvents.jobsFeed.pageViews.allPaid(this.props.texts.pageTitle);
  };
}

const mapStateToProps = (
  state: IApplicationState,
  ownProps: IJobsPageInjectedProps
): IAllPaidPageStoreProps => {
  const pageTranslations = state.translation.pages[PageKey.Jobs]!;
  const commonTranslations = state.translation.common;
  const userType = state.user.data!.type;

  const convertToTableItem = new JobItemToJobTableItemConverter(
    prepareJobItemToJobTableItemConverterTexts(
      pageTranslations,
      commonTranslations
    ),
    ownProps.productionTypes,
    ownProps.contractTypes
  ).convert;

  return {
    texts: {
      table: prepareTableTexts(commonTranslations),
      pageTitle: getPageTitle(commonTranslations, PageTitleKey.JobsFeedAllPaid),
    },
    isPerformer: userType === AccountType.Performer,
    findJobs: (...args) => jobService.find(...args),
    markAsRead: (...args) => jobService.markAsRead(...args),
    markAsUnread: (...args) => jobService.markAsUnread(...args),
    convertToTableItem,
    jobsFeed: state.jobsFeed,
  };
};

const mapDispatchToProps: MapDispatchToProps<IAllPaidPageActions> = {
  setLoading,
  showPopup,
  hidePopup,
  setJobsFeedData,
};

export const withJobsFilterOptions: IWithJobsFilterOptions = {
  tab: JobsPageTab.AllPaid,
  filterContractTypes(facetGroups) {
    return facetGroups.filter(
      (facetGroup) => facetGroup.code !== facetGroupCode.contractType.lowPay
    );
  },
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withLaunchDarklyConsumerHOC(),
  withJobsFilter<IAllPaidPageProps>(withJobsFilterOptions),
  withAccessControl({
    accountTypes: [AccountType.Performer],
  }),
  withTranslation()
)(AllPaidPage);
